import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`; // YYYY-MM-DD
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
};

// Debounce utility function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const DepositeandWithdraw_report = () => {
  const { roleName, data } = useAuth();
  const isSuperAdmin = roleName === 'superadmin';
  const userBranchId = data?.branchId;

  // State management
  const [allTransactions, setAllTransactions] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);
  const [personalMap, setPersonalMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [filterType, setFilterType] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [cashTypeFilter, setCashTypeFilter] = useState('all');
  const [managerIdFilter, setManagerIdFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Memoize the filter object
  const filterValues = useMemo(
    () => ({
      filterType,
      selectedDate,
      fromDate,
      toDate,
      transactionTypeFilter,
      cashTypeFilter,
      managerIdFilter,
    }),
    [filterType, selectedDate, fromDate, toDate, transactionTypeFilter, cashTypeFilter, managerIdFilter]
  );

  // Debounce the memoized filter values
  const debouncedFilters = useDebounce(filterValues, 300);

  // Track previous filter values
  const prevFiltersRef = useRef(null);

  // Reset to first page only when filter VALUES actually change
  useEffect(() => {
    const currentKey = JSON.stringify(debouncedFilters);
    if (prevFiltersRef.current !== null && prevFiltersRef.current !== currentKey) {
      setCurrentPage(1);
    }
    prevFiltersRef.current = currentKey;
  }, [debouncedFilters]);

  // Fetch staff details
  const fetchStaffDetails = useCallback(async () => {
    try {
      const baseFilter = isSuperAdmin ? {} : { branchId: userBranchId };
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/staff_details`, {
        params: { filter: JSON.stringify(baseFilter) },
      });

      const staff = response.data.data || [];
      setStaffDetails(staff);
    } catch (error) {
      console.error('Error fetching staff details:', error);
      setError('Failed to load staff details.');
    }
  }, [isSuperAdmin, userBranchId]);

  // Fetch personal details to get member names
  const fetchPersonalDetails = useCallback(async () => {
    try {
      const baseFilter = isSuperAdmin ? {} : { branchId: userBranchId };
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/personal_details`, {
        params: { filter: JSON.stringify(baseFilter) },
      });
      const people = response.data.data || [];
      const map = {};
      people.forEach((p) => { map[p.customerUniqueId] = p.name; });
      setPersonalMap(map);
    } catch (err) {
      console.error('Error fetching personal details:', err);
    }
  }, [isSuperAdmin, userBranchId]);

  // Fetch all transactions
  const fetchAllTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (!isSuperAdmin) {
        params.branchId = userBranchId;
      }

      // Determine date range based on filter type
      const today = new Date();
      if (filterType === 'daily' && selectedDate) {
        params.fromDate = selectedDate;
        params.toDate = selectedDate;
      } else if (filterType === 'weekly') {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        params.fromDate = start.toISOString().split('T')[0];
        params.toDate = today.toISOString().split('T')[0];
      } else if (filterType === 'dateRange' && fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      } else {
        // Default: Monthly
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        params.fromDate = start.toISOString().split('T')[0];
        params.toDate = today.toISOString().split('T')[0];
      }

      if (transactionTypeFilter && transactionTypeFilter !== 'all') {
        params.transactionType = transactionTypeFilter;
      }
      if (cashTypeFilter && cashTypeFilter !== 'all') {
        params.cashType = cashTypeFilter;
      }
      if (managerIdFilter && managerIdFilter !== 'all') {
        params.managerId = managerIdFilter;
      }

      const response = await axios.get(`${API_BASE_URL}/api/getModelData/transactions`, {
        params: params,
      });

      let transactions = response.data.data || [];

      // Parse and format dates
      transactions = transactions.map((tx) => ({
        ...tx,
        formattedDate: formatDate(tx.date),
        formattedTime: tx.createdAt
          ? new Date(tx.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
          : 'N/A',
        parsedDate: parseDate(tx.date),
        amount: parseFloat(tx.amount) || 0,
        cash_type: tx.cash_type?.toLowerCase(),
      }));

      setAllTransactions(transactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, userBranchId, filterType, selectedDate, fromDate, toDate, transactionTypeFilter, cashTypeFilter, managerIdFilter]);

  useEffect(() => {
    fetchAllTransactions();
    fetchStaffDetails();
    fetchPersonalDetails();
  }, [fetchAllTransactions, fetchStaffDetails, fetchPersonalDetails]);

  // Create a map for quick staff lookup
  const staffMap = useMemo(() => {
    const map = new Map();
    staffDetails.forEach((staff) => {
      map.set(staff.id, staff);
    });
    return map;
  }, [staffDetails]);

  // Filter transactions client-side
  const filteredTransactions = useMemo(() => {
    if (!allTransactions.length) return [];

    const today = new Date();
    let startDate, endDate;

    // Determine date range based on filter type
    if (debouncedFilters.filterType === 'daily' && debouncedFilters.selectedDate) {
      startDate = parseDate(debouncedFilters.selectedDate);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
    } else if (debouncedFilters.filterType === 'weekly') {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
    } else if (debouncedFilters.filterType === 'dateRange' && debouncedFilters.fromDate && debouncedFilters.toDate) {
      startDate = parseDate(debouncedFilters.fromDate);
      endDate = parseDate(debouncedFilters.toDate);
      if (startDate && endDate && startDate > endDate) {
        setError('From Date cannot be later than To Date.');
        return [];
      }
      endDate?.setHours(23, 59, 59, 999);
    } else {
      // Default: Monthly
      startDate = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    }

    if (!startDate || !endDate) return [];

    // Apply all filters
    return allTransactions.filter((tx) => {
      // Date filter
      const dateMatch = tx.parsedDate && tx.parsedDate >= startDate && tx.parsedDate <= endDate;

      // Transaction type filter
      const typeMatch =
        debouncedFilters.transactionTypeFilter === 'all' ||
        tx.transactionType === debouncedFilters.transactionTypeFilter;

      // Cash type filter
      const cashMatch =
        debouncedFilters.cashTypeFilter === 'all' ||
        tx.cash_type === debouncedFilters.cashTypeFilter;

      // Manager ID filter
      const managerMatch =
        debouncedFilters.managerIdFilter === 'all' ||
        tx.managerId === parseInt(debouncedFilters.managerIdFilter);

      return dateMatch && typeMatch && cashMatch && managerMatch;
    });
  }, [allTransactions, debouncedFilters]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);
  const totalAmount = filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0);

  const handleFilterChange = (e) => {
    const newFilterType = e.target.value;
    setFilterType(newFilterType);
    setError(null);
    if (newFilterType !== 'daily') setSelectedDate('');
    if (newFilterType !== 'dateRange') {
      setFromDate('');
      setToDate('');
    }
  };

  const exportToExcel = () => {
    const dataToExport = filteredTransactions.map((tx, index) => {
      const staff = staffMap.get(tx.managerId);
      return {
        'S.No': index + 1,
        'Member Name': personalMap[tx.customerUniqueId] || 'N/A',
        'Customer ID': tx.customerUniqueId,
        'Transaction Type': tx.transactionType,
        'Amount (₹)': tx.amount.toFixed(2),
        'Date': tx.formattedDate,
        'Time': tx.formattedTime,
        'Narration': tx.narration,
        'Branch ID': tx.branchId,
        'Account Type': tx.accountType,
        'Cash Type': tx.cash_type,
        'Manager': staff ? staff.name : 'N/A',
        'Manager ID': tx.managerId || 'N/A',
        'Transaction ID': tx.transactionId,
      };
    });

    let reportPeriod = filterType;
    if (filterType === 'dateRange') reportPeriod = `${fromDate}_to_${toDate}`;
    else if (filterType === 'daily') reportPeriod = selectedDate;

    const worksheetData = [
      [
        'S.No',
        'Member Name',
        'Customer ID',
        'Transaction Type',
        'Amount (₹)',
        'Date',
        'Time',
        'Narration',
        'Branch ID',
        'Account Type',
        'Cash Type',
        'Manager',
        'Manager ID',
        'Transaction ID',
      ],
      ...dataToExport.map((row) => Object.values(row)),
      [],
      ['Total Transactions:', '', '', totalAmount.toFixed(2)],
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c });
      if (worksheet[headerCell]) worksheet[headerCell].s = { font: { bold: true } };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions Report');
    XLSX.writeFile(workbook, `Transactions_Report_${reportPeriod}.xlsx`);
  };

  const refreshData = () => {
    fetchAllTransactions();
    fetchStaffDetails();
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* Filter Period */}
            <div className="relative min-w-[130px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Period
              </label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={handleFilterChange}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="monthly">Monthly</option>
                  <option value="daily">Daily</option>
                  <option value="dateRange">Date Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Date Inputs */}
            {filterType === 'daily' && (
              <div className="w-[145px] mt-1">
                <DatePicker
                  label="Select Date"
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date ? date.toISOString().split('T')[0] : '')}
                  placeholder="YYYY-MM-DD"
                />
              </div>
            )}

            {filterType === 'dateRange' && (
              <>
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="From Date"
                    selected={fromDate}
                    onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="YYYY-MM-DD"
                  />
                </div>
                <div className="w-[145px] mt-1">
                  <DatePicker
                    label="To Date"
                    selected={toDate}
                    onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="YYYY-MM-DD"
                  />
                </div>
              </>
            )}

            {/* Transaction Type Filter */}
            <div className="relative min-w-[130px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Tx Type
              </label>
              <div className="relative">
                <select
                  value={transactionTypeFilter}
                  onChange={(e) => setTransactionTypeFilter(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="all">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdraw">Withdrawal</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Cash Type Filter */}
            <div className="relative min-w-[130px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Cash Type
              </label>
              <div className="relative">
                <select
                  value={cashTypeFilter}
                  onChange={(e) => setCashTypeFilter(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="all">All Types</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="electronic">Electronic</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Manager Filter */}
            <div className="relative min-w-[160px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Manager/Staff
              </label>
              <div className="relative">
                <select
                  value={managerIdFilter}
                  onChange={(e) => setManagerIdFilter(e.target.value)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="all">All Staff</option>
                  {staffDetails.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={refreshData} variant="secondary" size="sm" disabled={loading} className="h-[38px] px-3 font-semibold text-xs">
              <i className="fa-solid fa-arrows-rotate mr-1.5"></i>
              Refresh
            </Button>
            <Button
              onClick={exportToExcel}
              variant="primary"
              size="sm"
              disabled={!filteredTransactions.length}
              className="shadow-sm h-[38px] px-4 font-bold"
            >
              <i className="fa-solid fa-file-excel mr-1.5"></i>
              Export Excel
            </Button>
          </div>
        }
      />
      {loading && !allTransactions.length && <Loader message="Loading transaction data..." />}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-triangle-exclamation"></i>
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-rose-900 hover:text-rose-950">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <ReportSummaryCard
          label="Total Transactions"
          value={filteredTransactions.length}
          bg="bg-[#DCEBFF]"
          border="border-[#2563EB]/25"
          accent="text-[#2563EB]"
          icon="fa-arrow-right-arrow-left"
        />
        <ReportSummaryCard
          label="Total Amount"
          value={`₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
          bg="bg-[#FFF0C7]"
          border="border-[#D97706]/25"
          accent="text-[#D97706]"
          icon="fa-vault"
        />
        <ReportSummaryCard
          label="Average Amount"
          value={`₹${(filteredTransactions.length ? totalAmount / filteredTransactions.length : 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
          bg="bg-[#E9DFFF]"
          border="border-[#7C3AED]/25"
          accent="text-[#7C3AED]"
          icon="fa-chart-line"
        />
      </div>

      {/* Data Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell align="center">S.No</Table.HeadCell>
              <Table.HeadCell align="left">Member Name</Table.HeadCell>
              <Table.HeadCell align="center">Customer ID</Table.HeadCell>
              <Table.HeadCell align="center">Type</Table.HeadCell>
              <Table.HeadCell align="right">Amount</Table.HeadCell>
              <Table.HeadCell align="center">Date</Table.HeadCell>
              <Table.HeadCell align="center">Time</Table.HeadCell>
              <Table.HeadCell align="left">Narration</Table.HeadCell>
              <Table.HeadCell align="center">Branch</Table.HeadCell>
              <Table.HeadCell align="center">Account Type</Table.HeadCell>
              <Table.HeadCell align="center">Cash Type</Table.HeadCell>
              <Table.HeadCell align="left">Manager</Table.HeadCell>
              <Table.HeadCell align="center">Transaction ID</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {paginatedData.length > 0 ? (
              paginatedData.map((tx, idx) => {
                const staff = staffMap.get(tx.managerId);
                return (
                  <Table.Row key={tx.id}>
                    <Table.Cell align="center">{(currentPage - 1) * rowsPerPage + idx + 1}</Table.Cell>
                    <Table.Cell align="left" className="font-semibold text-ink-800">
                      {personalMap[tx.customerUniqueId] || <span className="text-ink-400">N/A</span>}
                    </Table.Cell>
                    <Table.Cell align="center" className="font-semibold text-primary">{tx.customerUniqueId}</Table.Cell>
                    <Table.Cell align="center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        tx.transactionType === 'deposit'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`}>
                        {tx.transactionType}
                      </span>
                    </Table.Cell>
                    <Table.Cell align="right" className="font-bold text-ink-800">₹{tx.amount.toFixed(2)}</Table.Cell>
                    <Table.Cell align="center">{tx.formattedDate}</Table.Cell>
                    <Table.Cell align="center" className="text-xs text-ink-500">{tx.formattedTime}</Table.Cell>
                    <Table.Cell align="left" className="max-w-[150px] truncate" title={tx.narration}>{tx.narration}</Table.Cell>
                    <Table.Cell align="center">{tx.branchId}</Table.Cell>
                    <Table.Cell align="center">{tx.accountType}</Table.Cell>
                    <Table.Cell align="center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        tx.cash_type === 'cash' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                        tx.cash_type === 'online' ? 'bg-sky-50 text-sky-700 border border-sky-200' :
                        'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      }`}>
                        {tx.cash_type}
                      </span>
                    </Table.Cell>
                    <Table.Cell align="left">
                      {staff ? (
                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          {staff.name}
                        </span>
                      ) : (
                        <span className="text-ink-400">N/A</span>
                      )}
                    </Table.Cell>
                    <Table.Cell align="center" className="text-xs text-ink-400 font-mono">{tx.transactionId}</Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <Table.EmptyRow colSpan={13} message="No records found for the selected criteria" />
            )}
          </Table.Body>
        </Table>

        {/* Pagination */}
        {filteredTransactions.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-transparent border border-border-medium rounded px-1.5 py-1 focus:outline-none font-semibold text-ink-700"
              >
                {[5, 10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <span className="text-xs text-ink-500">
              Showing {((currentPage - 1) * rowsPerPage) + 1} to{' '}
              {Math.min(currentPage * rowsPerPage, filteredTransactions.length)} of{' '}
              {filteredTransactions.length} entries
            </span>

            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default DepositeandWithdraw_report;